import { ReactNode, useMemo } from 'react';

import {
  AspectRatio,
  Carousel,
  ConditionalWrapper,
  Group,
  ImageMantine,
  PlaceholderCard,
  SimpleGrid,
  Stack,
  Text,
  UnstyledButton,
} from 'ui';
import { IconArrowRight } from 'ui/Icons';

import classes from './MyFavoriteCarousel.module.css';

type MyFavoriteCarouselItemCardProps = {
  name: string;
  subtitle?: ReactNode | string;
  image: string;
  onClick?: () => void;
  aspectRatio?: number;
  isViewAll?: boolean;
  isLoading?: boolean;
  isSmallScreen?: boolean;
  alwaysShowInCarousel?: boolean;
};

const MyFavoriteCarouselItemCard = ({
  name,
  subtitle,
  image,
  onClick,
  aspectRatio = 2,
  isViewAll,
  isSmallScreen,
  alwaysShowInCarousel,
}: MyFavoriteCarouselItemCardProps) => {
  const content = isViewAll ? (
    <AspectRatio ratio={aspectRatio}>
      <PlaceholderCard onClick={onClick} bd="0">
        <Group align="center" gap="xs">
          <Text fz="sm" fw={500} lh="0">
            View All
          </Text>
          <IconArrowRight size="16" />
        </Group>
      </PlaceholderCard>
    </AspectRatio>
  ) : (
    <Stack
      gap="0"
      component={UnstyledButton}
      onClick={onClick}
      className={classes.MyFavoriteCarouselItemCard}
    >
      <AspectRatio
        ratio={aspectRatio}
        className={classes.MyFavoriteCarouselItemCard__imageContainer}
      >
        <ImageMantine src={image} alt={name} />
      </AspectRatio>
      <Stack p="sm" gap="4" flex="1">
        <Text c="gray.8" fz="xs" fw={600} lineClamp={2}>
          {name}
        </Text>
        {!!subtitle && typeof subtitle === 'string' ? (
          <Text c="gray" fz="10">
            {subtitle}
          </Text>
        ) : (
          subtitle
        )}
      </Stack>
    </Stack>
  );

  return (
    <ConditionalWrapper
      condition={!!alwaysShowInCarousel || !!isSmallScreen}
      wrapper={(children) => <Carousel.Slide mih="100%">{children}</Carousel.Slide>}
    >
      {content}
    </ConditionalWrapper>
  );
};

interface MyFavoriteCarouselProps {
  isSmallScreen?: boolean;
  /**
   * The items to display in the component.
   */
  items: MyFavoriteCarouselItemCardProps[];
  /**
   * The number of items to display in the component.
   * e.g. There are 10 items in the data set, but we only want to display 5.
   * @default 3
   */
  maxVisibleItems?: number;
  /**
   * The total number of items in the data set.
   * e.g. There are 10 items in the data set, so there are 10 total items.
   */
  numTotalItems?: number;
  /**
   * The function to call when the "View All" button is clicked. If provided,
   * the component will display a "View All" button as the last item in the carousel.
   * @returns void
   */
  onViewAllClick?: () => void;

  /**
   * The aspect ratio of the image. a value of 2 means a 2:1 aspect ratio.
   * @default 2
   */
  aspectRatio?: number;
  /**
   * If true, the component will always show the items in the carousel.
   * @default false - which means the component will show the items in a non-scrollable grid on larger screens.
   */
  alwaysShowInCarousel?: boolean;
  /**
   * The number of items to scroll in the carousel
   * and also the number of items that are shown per page of the carousel.
   *
   * @default 3
   */
  slidesToScroll?: number;
  /**
   * If true, the component will display a loading state
   * @default false
   */
  isLoading?: boolean;
}

const MyFavoriteCarousel = ({
  isSmallScreen,
  items,
  maxVisibleItems = 3,
  numTotalItems,
  onViewAllClick,
  aspectRatio = 2,
  alwaysShowInCarousel = false,
  slidesToScroll = 2,
}: MyFavoriteCarouselProps) => {
  const renderItems = useMemo(() => {
    const itemsToRender = [...items.slice(0, maxVisibleItems)];

    if (onViewAllClick && numTotalItems && numTotalItems > maxVisibleItems) {
      itemsToRender.push({
        name: '',
        image: '',
        onClick: onViewAllClick,
        isViewAll: true,
      });
    }

    return itemsToRender.map((item, index) => (
      <MyFavoriteCarouselItemCard
        key={index}
        {...item}
        aspectRatio={aspectRatio}
        isSmallScreen={isSmallScreen}
        alwaysShowInCarousel={alwaysShowInCarousel}
      />
    ));
  }, [
    items,
    maxVisibleItems,
    numTotalItems,
    onViewAllClick,
    aspectRatio,
    isSmallScreen,
    alwaysShowInCarousel,
  ]);

  const numItems = useMemo(
    () => (onViewAllClick ? maxVisibleItems + 1 : maxVisibleItems),
    [onViewAllClick, maxVisibleItems]
  );

  if (!isSmallScreen && !alwaysShowInCarousel) {
    return (
      <SimpleGrid cols={numItems} px="md">
        {renderItems}
      </SimpleGrid>
    );
  }

  return (
    <Carousel
      slidesToScroll={slidesToScroll}
      slideSize={`${100 / slidesToScroll}%`}
      slideGap="xs"
      align="start"
      withControls={false}
      withIndicators={true}
      classNames={{
        viewport: classes.MyFavoriteCarousel__viewport,
        indicators: classes.MyFavoriteCarousel__indicators,
        indicator: classes.MyFavoriteCarousel__indicator,
      }}
    >
      {renderItems}
    </Carousel>
  );
};

export { MyFavoriteCarousel };
export type { MyFavoriteCarouselItemCardProps, MyFavoriteCarouselProps };
